import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  FormControlLabel,
  Checkbox,
  Card,
  CardContent,
  Stack,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import WarningIcon from '@mui/icons-material/Warning';
import { useState } from 'react';
import type { DeletionImpact, DeletionCounts } from '../../hooks/useOrganizationDeletion';

interface DeletionImpactDialogProps {
  open: boolean;
  onClose: () => void;
  impact: DeletionImpact | undefined;
  isLoading: boolean;
  onConfirm: () => void;
  isDeleting?: boolean;
}

const COUNT_LABELS: Record<keyof DeletionCounts, string> = {
  users: 'Users',
  locations: 'Locations',
  products: 'Products',
  inventory: 'Inventory Records',
  sales: 'Sales',
  reservations: 'Reservations',
  carts: 'Shopping Carts',
  quotes: 'Quotes',
  invoices: 'Invoices',
  pricingTiers: 'Pricing Tiers',
  stockTransfers: 'Stock Transfers',
  usageRecords: 'Usage Records',
  b2bAccessRequests: 'B2B Access Requests',
  resellerProfiles: 'Reseller Profiles',
  deliveries: 'Deliveries',
  deliveryRoutes: 'Delivery Routes',
  inventoryMovements: 'Inventory Movements',
};

export const DeletionImpactDialog = ({
  open,
  onClose,
  impact,
  isLoading,
  onConfirm,
  isDeleting = false,
}: DeletionImpactDialogProps) => {
  const [confirmed, setConfirmed] = useState(false);

  const handleClose = () => {
    if (!isDeleting) {
      onClose();
      setConfirmed(false);
    }
  };

  const handleConfirm = () => {
    onConfirm();
  };

  const nonZeroCounts = impact?.counts
    ? Object.entries(impact.counts).filter(([, count]) => count > 0)
    : [];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1, bgcolor: 'warning.light', color: 'warning.contrastText' }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <WarningIcon />
          <Typography variant="h6">Delete Organization</Typography>
        </Stack>
      </DialogTitle>

      <DialogContent>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : impact ? (
          <Stack spacing={3} sx={{ mt: 2 }}>
            {/* Organization being deleted */}
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                You're about to delete:
              </Typography>
              <Card variant="outlined" sx={{ bgcolor: 'warning.lighter', borderColor: 'warning.main' }}>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="h6" fontWeight={600}>
                    {impact.organization.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    ID: {impact.organization.id}
                  </Typography>
                </CardContent>
              </Card>
            </Box>

            {/* Impact summary */}
            <Alert severity="warning" icon={<WarningIcon />}>
              <Typography variant="body2" fontWeight={600}>
                This action will move {impact.totalRecords.toLocaleString()} records to trash
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                All related data including users, products, sales, and invoices will be soft-deleted.
                You can restore the organization from the Trash page if needed.
              </Typography>
            </Alert>

            {/* Warnings */}
            {impact.warnings.length > 0 && (
              <Alert severity="info">
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  Important Notes:
                </Typography>
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {impact.warnings.map((warning, index) => (
                    <li key={index}>
                      <Typography variant="body2">{warning}</Typography>
                    </li>
                  ))}
                </ul>
              </Alert>
            )}

            {/* Detailed counts */}
            {nonZeroCounts.length > 0 && (
              <Box>
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  Affected Records:
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Entity Type</TableCell>
                        <TableCell align="right">Count</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {nonZeroCounts.map(([key, count]) => (
                        <TableRow key={key}>
                          <TableCell>
                            {COUNT_LABELS[key as keyof DeletionCounts] || key}
                          </TableCell>
                          <TableCell align="right">{count.toLocaleString()}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ bgcolor: 'action.hover' }}>
                        <TableCell>
                          <Typography fontWeight={600}>Total</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight={600}>
                            {impact.totalRecords.toLocaleString()}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Stack>
        ) : (
          <Alert severity="error">Failed to load deletion impact analysis</Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, bgcolor: confirmed ? 'warning.lighter' : 'background.default', flexDirection: 'column', alignItems: 'stretch', gap: 2 }}>
        {impact && (
          <FormControlLabel
            control={
              <Checkbox
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                disabled={isDeleting}
                color="warning"
              />
            }
            label={
              <Typography variant="body2">
                I understand that this will move {impact.totalRecords.toLocaleString()} records to trash
              </Typography>
            }
            sx={{ m: 0 }}
          />
        )}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button onClick={handleClose} disabled={isDeleting} variant="outlined">
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleConfirm}
            disabled={isDeleting || isLoading || !confirmed}
            startIcon={isDeleting ? <CircularProgress size={16} /> : <DeleteForeverIcon />}
          >
            {isDeleting ? 'Moving to Trash...' : 'Move to Trash'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
